import {
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { HotDocComponent } from "@components/hot-doc/hot-doc.component";
import { LabelComponent } from "@components/label/label.component";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { CellCoords } from "handsontable";
import { timer } from "rxjs";
import { Cellstyle, NumericCellChangeArg } from "~app/common/components/hot-doc/hotdoc/cell";
import { HotDoc, IHotColumnDefinition, IHotDocSettings } from "~app/common/components/hot-doc/hotdoc/doc";
import { ToastService } from "~app/common/components/toast/toast.service";
import { ProduktFordelningsmatrisTyper } from "~app/common/models/constants";
import { ProduktLangdfordelningDTO } from "~app/common/models/produkt/ProduktLangdfordelningDTO";
import { ProduktspecifikationDTO } from "~app/common/models/produkt/ProduktspecifikationDTO";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { DataExport } from "~app/common/services/dataExport";
import { MathUtils } from "~app/common/utils/math-utils";
import { ProduktDetailTexts } from "../../../produkt-detail.texts";
import { AddEditRowCell, FordelningsmatrisCell, FordelningsmatrisSumCell } from "./fordelningmatris-cells";
import { FordelningsmatrisDiameterfordelningModalComponent } from "./modals/diameterfordelning-modal/diameterfordelning-modal.component";
import { FordelningsmatrisLangdmodulModalComponent } from "./modals/langdmodul-modal/langdmodul-modal.component";

@Component({
    selector: "rask-matris",
    templateUrl: "./matris.component.html",
    styleUrls: ["./matris.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HotDocComponent,
        ButtonComponent,
        LabelComponent
    ]
})
export class MatrisComponent implements OnInit, OnChanges {
    canEdit: boolean;
    editIcon = "fas fa-edit";
    fmHotDoc: HotDoc;
    @Input() form: UntypedFormGroup;
    @Input() produktspecifikation: ProduktspecifikationDTO;
    selectedFordelningsmatrisCells: FordelningsmatrisCell[];
    texts = ProduktDetailTexts.fordelningsmatrisTab.texts;

    private readonly authenticationService = inject(AuthenticationService);
    private readonly modalService = inject(NgbModal);
    private readonly toastService = inject(ToastService);

    get canGenereraFranLangdmoduler() {
        return this.canEdit &&
            this.produktspecifikation.produktDiameterfordelningar.length <= 0 &&
            this.produktspecifikation.langdgransMin &&
            this.produktspecifikation.langdgransMax &&
            this.produktspecifikation.diaToppMin &&
            this.produktspecifikation.produktLangdmoduler.length > 0;
    }

    get isCellSelected() {
        return this.canEdit && this.selectedFordelningsmatrisCells && this.selectedFordelningsmatrisCells.length > 0;
    }

    setCelltyp = (typ: string) => {
        this.selectedFordelningsmatrisCells.forEach(c => {
            c.langdFordelning.typ = typ;
            if (typ === ProduktFordelningsmatrisTyper.forbjuden) {
                c.langdFordelning.andel = 0;
            }
        });

        this.form.controls.produktDiameterfordelningar.markAsDirty();
        this.fmHotDoc.rebind();
    };

    clearCell = () => this.setCelltyp(ProduktFordelningsmatrisTyper.inget);

    copyRader=(): void => {
        if (this.produktspecifikation.produktDiameterfordelningar?.length > 0) {
            const excel = new DataExport();

            excel.addRow(row => {
                row.addCell("\u2193 L / D \u2192");
                this.produktspecifikation.produktDiameterfordelningar.forEach(x => {
                    row.addCell(x.diamBotten.toLocaleString("sv"));
                });
            });
            this.produktspecifikation.produktDiameterfordelningar[0]?.produktLangdfordelningar?.forEach(plm => {
                excel.addRow(row => {
                    row.addCell(plm.langdBotten);

                    this.produktspecifikation.produktDiameterfordelningar.forEach(pdf => {
                        // eslint-disable-next-line sonarjs/no-nested-functions
                        const langdFordelning = pdf.produktLangdfordelningar.find(p => p.langdBotten === plm.langdBotten);
                        row.addCell(Math.round(langdFordelning.andel));
                    });
                });
            });
            excel.setToClipboard(() => this.toastService.add("Rader har kopierats till clipboard"));
        }
    };

    createHotDoc = (): HotDoc => {
        const colFactory = () => this.buildColumnDefinitions();
        const rowFactory = (doc: HotDoc) => this.buildRowDefinitions(doc);

        const hotDocSettings: IHotDocSettings = {
            raskSettings: {
                afterOnCellMouseDown: this.onAddOrEditDiameterfordelning
            },
            outsideClickDeselects: false,
            afterSelection: this.onCellsSelected
        };

        return new HotDoc(colFactory, rowFactory, () => hotDocSettings);
    };

    genereraFranLangdmoduler = (): void => {
        const langdfordelningar: ProduktLangdfordelningDTO[] = [];
        this.produktspecifikation.produktLangdmoduler.forEach(plm => {
            langdfordelningar.push({
                id: 0,
                typ: ProduktFordelningsmatrisTyper.inget,
                langdBotten: MathUtils.round(plm.modul),
                andel: 0
            });
        });

        this.produktspecifikation.produktDiameterfordelningar.push({
            id: 0,
            typ: ProduktFordelningsmatrisTyper.inget,
            produktLangdfordelningar: langdfordelningar,
            diamBotten: MathUtils.round(this.produktspecifikation.diaToppMin)
        });

        timer(0).subscribe(() => {
            this.fmHotDoc.rebind();
            this.form.controls.produktDiameterfordelningar.markAsDirty();
        });
    };

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.produktspecifikation && this.fmHotDoc) {
            timer(0).subscribe(() => {
                this.fmHotDoc.rebind();
            });
        }
    }

    ngOnInit(): void {
        this.canEdit = this.authenticationService.canEdit();
        timer(0).subscribe(() => {
            this.fmHotDoc = this.createHotDoc();
        });
    }

    setForbjuden = () => this.setCelltyp(ProduktFordelningsmatrisTyper.forbjuden);

    setMax = () => this.setCelltyp(ProduktFordelningsmatrisTyper.max);

    setMin = () => this.setCelltyp(ProduktFordelningsmatrisTyper.min);

    private readonly getColumnHeaderText = (name: string, icon?: string): string =>
        this.canEdit ? `<span class="pe-2">${name}</span><button class='btn btn-icon icon-btn accordion-trigger' type='button' ignore-readonly>` +
        // eslint-disable-next-line sonarjs/no-nested-conditional
            `<i class="column-icon ${icon ? icon : this.editIcon}"></i>` +
            `</button>` :
            `<span class="pe-2">${name}</span>`;

    private readonly getTableHeaderText = (): string => `<i class="fas fa-arrow-down"></i><span>${this.texts.hotHeader}</span><i class="fas fa-arrow-right"></i>`;

    private readonly replacePoint = (x: string) => x.replace(".", "_");

    private readonly buildColumnDefinitions = (): IHotColumnDefinition[] => {
        const columns: IHotColumnDefinition[] = [];
        if (this.canEdit) {
            columns.push({
                header: "",
                width: 30,
                readOnly: true,
                cellName: "settings"
            });
        }

        columns.push({
            header: this.getTableHeaderText(),
            width: 140,
            readOnly: true,
            cellName: "rowheader"
        });

        if (this.produktspecifikation.produktDiameterfordelningar?.length > 0) {
            const diameterkolumner = this.produktspecifikation.produktDiameterfordelningar.map(p => ({

                header: this.getColumnHeaderText(`${p.diamBotten} -`, this.editIcon),
                width: 100,
                readOnly: false,
                cellName: this.replacePoint(`diameter-${p.diamBotten.toString()}`)
            }));

            columns.push(...diameterkolumner);
        }

        columns.push({
            header: this.getColumnHeaderText("", "fas fa-plus"),
            width: 100,
            readOnly: true,
            cellName: `summa`
        });

        return columns;
    };

    private readonly onModalResult = (modalRef: NgbModalRef) => {
        modalRef.result.then(result => {
            if (result) {
                this.fmHotDoc.rebind();
                this.form.markAsDirty();
            }
        }, () => {
            // Fånga error
        });
    };

    private readonly onAddOrEditLangdmodul = (langd?: number): void => {
        const modalRef = this.modalService.open(FordelningsmatrisLangdmodulModalComponent, {
            keyboard: false,
            centered: true,
            backdrop: "static",
            size: "lg"
        });
        modalRef.componentInstance.min = this.produktspecifikation.langdgransMin;
        modalRef.componentInstance.max = this.produktspecifikation.langdgransMax;
        modalRef.componentInstance.title = langd ? this.texts.editLangd : this.texts.laggTillLangd;
        modalRef.componentInstance.diameterfordelningar = this.produktspecifikation.produktDiameterfordelningar;
        modalRef.componentInstance.inputLangd = langd;

        this.onModalResult(modalRef);
    };

    private readonly onUpdateProduktDiameterfordelning = (change: NumericCellChangeArg, langdfordelning: ProduktLangdfordelningDTO) => {
        langdfordelning.andel = change.newValue;
        this.form.controls.produktDiameterfordelningar.markAsDirty();
        timer(10).subscribe(() => {
            change.complete();
        });
    };

    // eslint-disable-next-line sonarjs/reduce-initial-value
    private readonly summera = (varden: number[]): number => varden && varden.length > 0 ? varden.reduce((a, b) => a + b) : 0;

    /* eslint-disable no-bitwise */
    private readonly buildRowDefinitions = (doc: HotDoc): void => {
        this.produktspecifikation.produktDiameterfordelningar[0]?.produktLangdfordelningar?.forEach(plm => {
            doc.createRow(row => {
                if (this.canEdit) {
                    const settingsCell = new AddEditRowCell(
                        Cellstyle.Readonly | Cellstyle.AlignLeft | Cellstyle.VerticalAlignMiddle,
                        { rowSpan: 1 },
                        this.editIcon,
                        () => this.onAddOrEditLangdmodul(plm.langdBotten));

                    row.add("settings", settingsCell);
                }

                row.addText("rowheader", `${plm.langdBotten} -`, Cellstyle.Readonly);
                let summa = 0;
                let kolumner = 0;

                this.produktspecifikation.produktDiameterfordelningar.forEach(pdf => {
                    // eslint-disable-next-line sonarjs/no-nested-functions
                    const langdFordelning = pdf.produktLangdfordelningar.find(p => p.langdBotten === plm.langdBotten);
                    const cell = new FordelningsmatrisCell(
                        langdFordelning,
                        Cellstyle.Default,
                        // eslint-disable-next-line sonarjs/no-nested-functions
                        arg => this.onUpdateProduktDiameterfordelning(arg, langdFordelning));

                    row.add(this.replacePoint(`diameter-${pdf.diamBotten}`), cell);

                    summa += langdFordelning.andel;
                    kolumner++;
                });

                const sumCell = new FordelningsmatrisSumCell(summa / kolumner, false);
                row.add(`summa`, sumCell);
            });
        });

        doc.createRow(row => {
            if (this.canEdit) {
                const settingsCell = new AddEditRowCell(
                    Cellstyle.Readonly | Cellstyle.AlignLeft | Cellstyle.VerticalAlignMiddle,
                    { rowSpan: 1 },
                    "fas fa-plus",
                    () => this.onAddOrEditLangdmodul(null));

                row.add("settings", settingsCell);
            }

            row.addText("rowheader", "", Cellstyle.Readonly);

            if (this.produktspecifikation.produktDiameterfordelningar.length > 0) {
                this.produktspecifikation.produktDiameterfordelningar.forEach(p => {
                    const summa = this.summera(p.produktLangdfordelningar.map(pp => pp.andel));
                    row.add(this.replacePoint(`diameter-${p.diamBotten.toString()}`), new FordelningsmatrisSumCell(summa, true));
                });
            }

            let sumOfSums = 0;
            doc.rows?.forEach(r => {
                sumOfSums += (r.cells[r.cells.length - 1] as FordelningsmatrisSumCell).value;
            });
            row.add("summa", new FordelningsmatrisSumCell(sumOfSums, false));
        });
    };

    private readonly onAddOrEditDiameterfordelning = (event: MouseEvent, coords: CellCoords, th: any): void => {
        if (event.target instanceof Element && event.target.className.indexOf("column-icon") >= 0) {
            const diameter = th.innerText.length > 0 ? +(th.innerText as string).replace("-", "").trim() : null;
            const modalRef = this.modalService.open(FordelningsmatrisDiameterfordelningModalComponent, {
                keyboard: false,
                centered: true,
                backdrop: "static",
                size: "lg"
            });
            modalRef.componentInstance.min = this.produktspecifikation.diaToppMin;
            modalRef.componentInstance.max = this.produktspecifikation.diaToppMax;
            modalRef.componentInstance.title = diameter ? this.texts.editDiameter : this.texts.laggTillDiameter;
            modalRef.componentInstance.diameterfordelningar = this.produktspecifikation.produktDiameterfordelningar;
            modalRef.componentInstance.inputDiameter = diameter;

            this.onModalResult(modalRef);
        }
    };

    private readonly onCellsSelected = (row: number, col: number, row2: number, col2: number, preventScrolling: any, selectionLayerLevel: number) => {
        const cells: FordelningsmatrisCell[] = [];
        row = Math.max(0, row);
        const startRow = row > row2 ? row2 : row;
        const slutRow = row2 >= row ? row2 : row;
        const startCol = col > col2 ? col2 : col;
        const slutCol = col2 >= col ? col2 : col;

        for (let i = startRow; i <= slutRow; i++) {
            for (let j = startCol; j <= slutCol; j++) {
                const cell = this.fmHotDoc.rows[i].cells[j];
                if (cell instanceof FordelningsmatrisCell) {
                    cells.push(this.fmHotDoc.rows[i].cells[j] as FordelningsmatrisCell);
                }
            }
        }

        this.selectedFordelningsmatrisCells = cells;
    };
}
